import React, {useEffect, useState} from "react";
import {useFetch} from "../../hooks/useFetch";
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Tag from "antd/lib/tag";
import MonthSelect from "../../components/MonthSelect";
import moment from 'moment';
import LoaderOverlay from "shared/components/LoaderOverlay";
import {Tooltip, Button, notification} from "antd";
import InvoiceShowModal, {Invoice} from "./InvoiceGetModal";
import InvoiceEmailModal from "views/client/clientHistoricInvoicing/InvoiceEmailModal";
import EditInvoiceModal from "views/client/clientHistoricInvoicing/EditInvoiceModal";
import {MouseEvent} from "react"
import BaseUrlConstants from "shared/constants/BaseUrlConstants";
import {getAuthToken, getUrlFormatedToken} from "shared/contexts/AuthContext";
import { EditOutlined, FilePdfOutlined, EyeOutlined, MailOutlined } from '@ant-design/icons';
import { FilteredTable } from "shared/components/table/FilterDropdown";
import ArrayService from "shared/services/ArrayService";
import StatusTag, {statusInvoiceLabelMap, statusLabelMap,} from "shared/components/StatusTag";
import { statusInvoiceColorMap } from 'shared/components/StatusInvoiceTag';
import StatusInvoiceTag from "shared/components/StatusInvoiceTag";
import StatusTiersPrestationTag from "shared/components/StatusTiersPrestationTag";
import './invoices.css';

const GET_DATA = gql`
    query getData($periode: String!) {
        commandInvoices(periode: $periode) {
            id
            client {
                id
                firstName
                lastName
                defaultDeliveryZone {
                    id
                    name
                    color
                    defaultDeliveryMan {
                        id
                        firstName
                        lastName
                    }
                }
            }
            periode
            status
            nameInvoice
            nameCreditNote
            apaPrice
            invoiceStatus
            meals {
                id
                isInvoiced
            }
            sentAt
            priceTotal
            priceTotalPaid
            priceTotalDue
            commandInvoiceUrssafStatuses {
                id
                statusCode
            }
            commandInvoiceEmails{
                id
                sentAt
            }
        }
    }
`;

const SEND_INVOICES_PENNYLANE = gql`
    mutation exportInvoicesToPennylane($ids: [Int!]) {
        exportInvoicesToPennylane(ids: $ids) {
            id
        }
    }
`;

interface CommandInvoiceUrssafStatus {
    id: number;
    statusCode: string;
}


export default function Invoices()  {

    const [monthValue, setMonthValue] = useState(nowDate() as string);
    const [selectionType] = useState('checkbox' as any);
    const [invoiceId, setInvoiceid] = useState(null as number | null);
    const [invoice, setInvoiceId] = useState(null as number | null);
    const [invoiceIdd, setInvoiceIdd] = useState(null as number | null);
    const [idInvoice, setIdInvoice] = useState([] as number[] | null);
    const [commandInvoices, setCommandInvoices] = useState<Array<CommandInvoice>>([]);
    const { loading, error, data, refetch } = useQuery<{ 
        commandInvoices : CommandInvoice[];
    }>(
        GET_DATA,
        {
            variables: {
                periode: monthValue,
            },
        },
    );

    const statusDbToFront: Record<number, 'validated' | 'exported' | 'non_validated'> = {
        0: 'non_validated',
        1: 'validated',
        2: 'exported',
    };

    const hasInvoicedMeal = (commandInvoice: CommandInvoice) =>
        Array.isArray(commandInvoice?.meals) &&
        commandInvoice.meals.some((m) => Number(m?.isInvoiced) === 1);

    const getLastUrssafStatus = (
        statuses?: CommandInvoiceUrssafStatus[] | null
    ): CommandInvoiceUrssafStatus | null => {
        if (!Array.isArray(statuses) || statuses.length === 0) {
            return null;
        }

        return [...statuses].sort((a, b) => b.id - a.id)[0];
    };

    const columns = [
        {
            title: <strong>Statut facturation</strong>,
            dataIndex: 'status',
            key: 'status',
            align: 'center',

            // Tri
            sorter: ArrayService.cmpBy(
                (i: CommandInvoice) => statusInvoiceLabelMap[statusDbToFront[i.status]]
            ),

            // Pour le filtrage : renvoie directement la valeur de BDD (0,1,2)
            getFilterValue: (i: CommandInvoice) => i.status,

            filter: 'select',

            // Options du filtre : même type que BDD (0,1,2)
            filterOptions: [
                { value: 0, label: <StatusInvoiceTag statusInvoice="non_validated" /> },
                { value: 1, label: <StatusInvoiceTag statusInvoice="validated" /> },
                { value: 2, label: <StatusInvoiceTag statusInvoice="exported" /> },
            ],

            // Rendu dans la colonne : on mappe la valeur de BDD (0,1,2) vers front
            render: (_: any, invoice: CommandInvoice) => (
                <StatusInvoiceTag statusInvoice={statusDbToFront[invoice.status]} />
            ),
        },
        {
            title: <strong>Envoyé au client</strong>,
            dataIndex: 'commandInvoiceEmails',
            key: 'commandInvoiceEmails',
            sorter: ArrayService.cmpBy(
                (i: CommandInvoice) =>
                    statusInvoiceLabelMap[i.commandInvoiceEmails?.length > 0 ? 'disabled' : 'enabled'],
            ),
            getFilterValue: (i: CommandInvoice) => (i.commandInvoiceEmails?.length > 0 ? 'disabled' : 'enabled'),
            filter: 'select',
            filterOptions: Object.keys(statusInvoiceLabelMap).map((commandInvoiceEmails) => ({
                value: commandInvoiceEmails,
                label: <Tag color={commandInvoiceEmails === 'disabled' ? 'green' : 'volcano'}>{commandInvoiceEmails === "disabled" ? 'OUI' : 'NON'}</Tag>,
            })),
            render: (_: any, invoice: CommandInvoice) => (
                <StatusInvoiceTag statusInvoice={invoice.commandInvoiceEmails?.length > 0 ? 'sent' : 'not_sent'} />
            ),
        },
        {
            title: <strong>Nom client</strong>,
            key: 'client',
            sorter: true,
            getFilterValue: (row: CommandInvoice) =>
                row.client &&
                `${row.client.firstName} ${row.client.lastName}`,
            filter: 'text',
            render: (row: CommandInvoice) =>
                row.client && `${row.client.firstName} ${row.client.lastName.toUpperCase()}`,
        },
        {
            title: <strong>Nom Facture</strong>,
            dataIndex: 'nameInvoice',
            key: 'nameInvoice',
            align: 'center',
        },
        {
            title: <strong>Statut Paiement</strong>,
            dataIndex: 'invoiceStatus',
            key: 'invoiceStatus',
            align: 'center',
            sorter: ArrayService.cmpBy(
                (i: CommandInvoice) =>
                    statusInvoiceLabelMap[i.invoiceStatus == 1 ? 'disabled' : 'enabled'],
            ),
            getFilterValue: (i: CommandInvoice) => (i.invoiceStatus == 1 ? 'disabled' : 'enabled'),
            filter: 'select',
            filterOptions: Object.keys(statusInvoiceLabelMap).map((CommandInvoice) => ({
                value: CommandInvoice,
                label: <Tag color={CommandInvoice === 'disabled' ? 'green' : 'volcano'}>{CommandInvoice === "disabled" ? 'PAYE' : 'NON PAYE'}</Tag>,
            })),
            render: (_: any, invoice: CommandInvoice) => (
                <StatusInvoiceTag statusInvoice={invoice.invoiceStatus == 1 ? 'enabled' : 'disabled'} />
            ),
        },
        {
            title: <strong>Tournée</strong>,
            key: 'defaultDeliveryMan',
            sorter: true,
            getFilterValue: (row: CommandInvoice) =>
                row.client &&
                `${row.client.defaultDeliveryZone?.defaultDeliveryMan.firstName} ${row.client.defaultDeliveryZone?.defaultDeliveryMan.lastName}`,
            filter: 'text',
            render: (row: CommandInvoice) =>
                row.client &&
                `${row.client.defaultDeliveryZone?.defaultDeliveryMan.firstName} ${row.client.defaultDeliveryZone?.defaultDeliveryMan.lastName.toUpperCase()}`,
        },
        {
            title: <strong>Montant APA TTC</strong>,
            dataIndex: 'apaPrice',
            key: 'apaPrice',
            align: 'center',
            render: (price: number | null) => (price !== null && price !== undefined ? price.toFixed(2) : "N/A"),
        },
        {
            title: <strong>Statut tiers prestation</strong>,
            dataIndex: 'commandInvoiceUrssafStatuses',
            key: 'commandInvoiceUrssafStatuses',
            align: 'center' as const,
            render: (_: any, invoice: CommandInvoice) => {
                const lastStatus = getLastUrssafStatus(invoice.commandInvoiceUrssafStatuses);

                if (!lastStatus) {
                    return <span>Non envoyé</span>;
                }

                return <StatusTiersPrestationTag statusTiers={lastStatus.statusCode} />;
            },
        },
        {
            title: <strong>Montant Total TTC</strong>,
            dataIndex: 'priceTotal',
            key: 'priceTotal',
            align: 'center',
            render: (price: number | null) => (price !== null && price !== undefined ? price.toFixed(2) : "N/A"),
        },
        {
            title: <strong>Actions</strong>,
            key: 'action',
            align: 'center',
            render: (_: any, commandInvoice: CommandInvoice) => {
                const canDownloadPdf = hasInvoicedMeal(commandInvoice);
                console.log(canDownloadPdf);
            return (
                <span className="__actions">
                    <Tooltip title="Détail de facturation">
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<EyeOutlined/>}
                            ghost
                            style={{marginRight: 8}}
                            onClick={() => setInvoiceid(commandInvoice.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Editer la facture">
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<EditOutlined/>}
                            ghost
                            style={{marginRight: 8}}
                            onClick={() => setInvoiceId(commandInvoice.id)}
                        />
                    </Tooltip>
                    <Tooltip
                        title={
                            canDownloadPdf
                                ? "Facture PDF"
                                : "PDF indisponible : aucun repas facturé"
                        }
                    >
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<FilePdfOutlined />}
                            ghost
                            style={{ marginRight: 8 }}
                            disabled={!canDownloadPdf}
                            onClick={() => {
                                const url = `${BaseUrlConstants.BASE_URL}api/invoices${commandInvoice.id}.pdf`;
                                window.open(`${url}?X-AUTH-TOKEN=${getUrlFormatedToken()}`, '_blank');
                                refetch();
                            }}
                        />
                    </Tooltip>
                    <Tooltip
                        title={
                            canDownloadPdf
                                ? "Avoir PDF"
                                : "PDF indisponible : aucun repas facturé"
                        }
                    >
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<FilePdfOutlined/>}
                            ghost
                            disabled={!canDownloadPdf}
                            style={{marginRight: 8}}
                            onClick={() => {
                                const url = `${BaseUrlConstants.BASE_URL}api/avoir${commandInvoice.id}.pdf`;
                                /* global window */
                                window.open(
                                    `${url}?X-AUTH-TOKEN=${getUrlFormatedToken()}`,
                                    '_blank',
                                );
                            }}
                        />
                    </Tooltip>
                    {commandInvoice.nameInvoice ?
                        <Tooltip title="Envoyer la facture par E-mail">
                            <Button
                                type="primary"
                                shape="circle"
                                icon={<MailOutlined/>}
                                ghost
                                style={{marginRight: 8}}
                                onClick={() => setInvoiceIdd(commandInvoice.id)}
                            />
                        </Tooltip>
                        : null}
                </span>
            )
            },
        },
    ];
 
    useEffect(() => {
        refetch();
        if (data && data.commandInvoices) {
            setCommandInvoices(data.commandInvoices);
        }
    }, [data, refetch]);

    const rowSelection = {
        onChange: (selectedRowKeys: any) => {
            setIdInvoice(selectedRowKeys)
        },
        getCheckboxProps: (invoice: CommandInvoice) => ({
            disabled: invoice.status != 1, // Column configuration not to be checked
        }),
    };

    const [exportInvoicesToPennylane] = useMutation(SEND_INVOICES_PENNYLANE, {

        variables: {
            ids: idInvoice
        },

        onCompleted: () => {
            refetch();
            notification.success({ message: 'Les factures selectionnées ont été envoyées sur Pennylane' });

        },
        onError: (err: Error) => {
            notification.error({
                message: "Erreur lors de l'enregistrement",
                description: err.message,
            });
        },
    });

    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().slice(0, 10);
    const fileNameExportPrelevement = `Export_facturation_prelevement_sans_apa_${formattedDate}.csv`;
    const fileNameExportPrelevement1 = `Export_facturation_prelevement_avec_apa_${formattedDate}.csv`;
    const handleExportFacturationPrelevement = async (e: MouseEvent) => {
        e.preventDefault()
        try {
            if (data) {
                await fetch(
                    `${
                        BaseUrlConstants.BASE_URL
                    }api/invoices/export_prelevement/0/${monthValue}?X-AUTH-TOKEN=${getUrlFormatedToken()}`,
                ).then((res: any) =>
                    res.blob().then((blob: any) => {
                        downloadBlob(blob, `${fileNameExportPrelevement}`);
                        if (res?.status === 200) {
                            // window.location.reload();
                            notification.success({message: 'Le fichier des clients sans APA a été crée'});
                        } else {
                            notification.error({message: res.error.message});
                        }
                    }),
                );
            }
        } catch (error) {
            console.error('Error exporting invoices:', error);
            notification.error({message: 'Une erreur s\'est produite lors de l\'export des factures.'});
        }
    }

    const handleExportFacturationPrelevement1 = async (e: MouseEvent) => {
        e.preventDefault()
        try {
            if (data) {
                await fetch(
                    `${
                        BaseUrlConstants.BASE_URL
                    }api/invoices/export_prelevement/1/${monthValue}?X-AUTH-TOKEN=${getUrlFormatedToken()}`,
                ).then((res: any) =>
                    res.blob().then((blob: any) => {
                        downloadBlob(blob, `${fileNameExportPrelevement1}`);
                        if (res?.status === 200) {
                            // window.location.reload();
                            notification.success({message: 'Le fichier des clients avec APA a été crée'});
                        } else {
                            notification.error({message: res.error.message});
                        }
                    }),
                );
            }
        } catch (error) {
            console.error('Error exporting invoices:', error);
            notification.error({message: 'Une erreur s\'est produite lors de l\'export des factures.'});
        }
    }

    const fileName = `relevé-facturation-${formattedDate}.csv`;
    const handleExportFactures = async (e: MouseEvent) => {
        e.preventDefault()
        try {
        if (data) {
            await fetch(
                `${
                    BaseUrlConstants.BASE_URL
                }api/invoices/export/invoices/${monthValue}?X-AUTH-TOKEN=${getUrlFormatedToken()}`,
            ).then((res: any) =>
                res.blob().then((blob: any) => {
                    downloadBlob(blob, `${fileName}`);
                    if (res?.status === 200) {
                       // window.location.reload();
                        notification.success({message: 'Les factures ont été exportées en CSV'});
                    } else {
                        notification.error({message: res.error.message});
                    }
                }),
            );
        }
        } catch (error) {
            console.error('Error exporting invoices:', error);
            notification.error({message: 'Une erreur s\'est produite lors de l\'export des factures.'});
        }
    }

    const handleExportPrelevements = (e:any) => {
        handleExportFacturationPrelevement(e);
        handleExportFacturationPrelevement1(e);
    };
    

    return (
        <div className="senior-commands">
            <div className="__header">
                <h2>Facturation</h2>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <MonthSelect
                        onChange={ (v) => {setMonthValue(v);} }
                        value={monthValue}
                    />
                </div>
                <div>
                    <div className="actions-container">
                        <div className="actions-left">
                        </div>
                        <div className="actions-right">
                            <Button
                                onClick={() => exportInvoicesToPennylane()}
                                type="primary"
                        htmlType="submit"
                        shape="round"
                        >
                            Envoyer les factures sur Pennylane
                        </Button>
                        </div>
                    </div>
                </div>
            </div>
            
            <div>
            {loading && <LoaderOverlay loading/>}
                <FilteredTable
                    rowSelection={{
                        type: selectionType,
                        ...rowSelection,
                    }}
                    rowKey="id"
                    dataSource={commandInvoices} 
                    columns={columns}
                />
            </div>
            {invoiceId ? (
                <InvoiceShowModal
                    invoiceId={invoiceId}
                    visible={!!invoiceId}
                    onClose={() => setInvoiceid(0)}
                />
            ) : null}
            <EditInvoiceModal
                invoiceId={invoice}
                visible={!!invoice}
                onClose={() => setInvoiceId(0)}
            />
            {invoiceIdd ? (
            <InvoiceEmailModal
                invoiceId={invoiceIdd}
                visible={!!invoiceIdd}
                onClose={() => setInvoiceIdd(0)}
            />
        ) : null}
        </div>
    )
}

const nowDate = (): string => {
    return moment().format('MM-YYYY');
}

function downloadBlob(blob: any, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}
